import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import Logo from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import NotificationBell from "@/components/NotificationBell";
import { titleCase } from "@/lib/format";
import { LayoutDashboard, MessagesSquare, ClipboardCheck, BookOpenCheck, Crown, Shield, LogOut, FileText, Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, id: "nav-dashboard" },
    { to: "/coaches", label: t("nav.coaches"), icon: MessagesSquare, id: "nav-coaches" },
    { to: "/tests", label: t("nav.tests"), icon: ClipboardCheck, id: "nav-tests" },
    { to: "/exams", label: "TCF Mocks", icon: FileText, id: "nav-exams" },
    { to: "/samples", label: t("nav.samples"), icon: BookOpenCheck, id: "nav-samples" },
    { to: "/premium", label: t("nav.premium"), icon: Crown, id: "nav-premium" },
  ];
  if (user?.role === "admin") links.push({ to: "/admin", label: t("nav.admin"), icon: Shield, id: "nav-admin" });

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <header className="sticky top-0 z-40 glass border-b border-border" data-testid="app-navbar">
      <div className="max-w-6xl mx-auto px-4 sm:px-5 h-16 flex items-center justify-between gap-3">
        <Link to="/dashboard" data-testid="nav-logo" className="shrink-0"><Logo size={32} textClass="text-base sm:text-lg" /></Link>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <Link key={l.to} to={l.to} data-testid={l.id}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-full text-[13px] font-medium transition-colors whitespace-nowrap ${
                  isActive(l.to) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}>
                <Icon className="h-4 w-4" /><span className="hidden xl:inline">{l.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <LanguageToggle />
          <NotificationBell />
          <div className="hidden md:flex flex-col items-end leading-tight">
            <span className="text-sm font-semibold max-w-[120px] truncate" data-testid="nav-user-name">{titleCase(user?.name)}</span>
            <span className="text-xs text-muted-foreground">{user?.is_premium ? t("nav.premiumBadge") : t("nav.target") + " " + user?.target_level}</span>
          </div>
          <button onClick={handleLogout} data-testid="nav-logout-btn" title={t("nav.logout")}
            className="hidden sm:grid h-9 w-9 place-items-center rounded-full bg-secondary hover:bg-destructive hover:text-destructive-foreground transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
          {/* Hamburger */}
          <button onClick={() => setOpen((o) => !o)} data-testid="nav-menu-toggle" aria-label="Menu"
            className="lg:hidden h-9 w-9 grid place-items-center rounded-full bg-secondary hover:bg-accent transition-colors">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile / tablet dropdown menu */}
      {open && (
        <nav className="lg:hidden border-t border-border bg-card px-4 py-3 space-y-1" data-testid="nav-mobile-menu">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <Link key={l.to} to={l.to} data-testid={`m-${l.id}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive(l.to) ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
                }`}>
                <Icon className="h-4 w-4" />{l.label}
              </Link>
            );
          })}
          <button onClick={handleLogout} data-testid="m-nav-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-destructive hover:bg-destructive/10">
            <LogOut className="h-4 w-4" /> {t("nav.logout")}
          </button>
        </nav>
      )}
    </header>
  );
}
